'use client'

import {ProductFacetSkeleton} from './ProductFacetSkeleton'

import {useCallback, useDeferredValue, useEffect, useMemo, useRef, useState} from 'react'
import {usePathname, useSearchParams} from 'next/navigation'
import {FormattedMessage, useIntl} from 'react-intl'
import {
  ProductFacetSidebar,
  type ProductFacetDefinition,
} from '@/components/products/ProductFacetSidebar'
import {MobileFacetSheet} from '@/components/products/MobileFacetSheet'
import {ActiveFilterBadges} from '@/components/products/ActiveFilterBadges'
import {ProductsGrid} from '@/components/products/ProductsGrid'
import {LoadMoreButton} from '@/components/products/LoadMoreButton'
import {readProductSearchResponse} from './readProductSearchResponse'
import {Skeleton} from '@/components/ui/skeleton'
import type {ProductFilterOption} from '@/components/products/ProductCatalogFilters'
import {buildFilterUseEvent, pushDataLayerEvent} from '@/lib/analytics'
import {productCanonicalPathForSite} from '@/lib/productCanonical'
import {isProductDiscontinued} from '@/components/products/productDisplay'
import type {ProductSearchDocument} from '@/lib/search/types'
import type {CanonicalSettingsDocument} from '@/types/sanity'
import type {SiteLite} from '@/multisite/types'
import {markActiveCampaignProducts} from '@/lib/campaignProducts'

type ProductCatalogClientProps = {
  site: SiteLite
  canonicalSettings?: CanonicalSettingsDocument | null
  locale: string
  initialProducts: ProductSearchDocument[]
  initialTotal: number
  facetTotal: number
  productTypes: ProductFilterOption[]
  query?: string
  siteBrandKeys: string[]
  siteMarketKeys: string[]
  initialSelectedBrands: string[]
  initialSelectedMarkets: string[]
  initialSelectedProductTypes: string[]
  initialVisibleCount: number
  defaultVisibleCount: number
  maxVisibleCount: number
  /** JO2-247: product ids listed on an active campaign for the site. */
  campaignProductIds?: string[]
}

type FilterState = {
  brands: string[]
  markets: string[]
  productTypes: string[]
  // JO2-247 "On Campaign": ['1'] when on. One option, so it reuses the facet UI.
  campaign: string[]
}

type FilterCriteria = {
  brands: Set<string>
  markets: Set<string>
  productTypes: Set<string>
  campaign: Set<string>
}

const filterStateKey = (filters: FilterState) =>
  JSON.stringify({
    brands: [...filters.brands].sort(),
    markets: [...filters.markets].sort(),
    productTypes: [...filters.productTypes].sort(),
    campaign: [...filters.campaign].sort(),
  })

const FILTER_SYNC_DEBOUNCE_MS = 120

// Reused instead of inline `[]` literals wherever a locked filter field is zeroed
// out, so repeated calls don't manufacture a new array reference each time —
// that reference churn was defeating memoization on `buildSearchParams` and
// spuriously re-triggering the filter-sync effect (see loadMore/updateFilters).
const EMPTY_STRING_ARRAY: string[] = []
const CAMPAIGN_ON: string[] = ['1']

const unique = (values: string[]) => Array.from(new Set(values.filter(Boolean)))

const sortFilterOptionsByLabel = (options: ProductFilterOption[], locale?: string) =>
  [...options].sort((left, right) =>
    left.label.localeCompare(right.label, locale, {numeric: true, sensitivity: 'base'}),
  )

const productTypeLabelForProduct = (product: ProductSearchDocument, locale: string) => {
  const labels = product.productType?.labels
  const localeKey = locale.replace('-', '')
  const directLabel =
    labels && typeof labels === 'object' ? labels[localeKey as keyof typeof labels] : undefined
  return (
    directLabel || product.productType?.title || product.productTypeKey || product.productType?.key
  )
}

const marketLabel = (value: string, locale: string) => {
  const normalized = value.toUpperCase()
  if (normalized === 'GLOBAL') return 'Global'

  try {
    const formatter = new Intl.DisplayNames([locale], {type: 'region'})
    return formatter.of(normalized) || normalized
  } catch {
    return normalized
  }
}

const productTypeKeyForProduct = (product: ProductSearchDocument) =>
  product.productTypeKey || product.productType?.key || ''

const criteriaFromFilters = (filters: FilterState): FilterCriteria => ({
  brands: new Set(filters.brands),
  markets: new Set(filters.markets),
  productTypes: new Set(filters.productTypes),
  campaign: new Set(filters.campaign),
})

const brandSortPriority = (brandKey?: string | null) =>
  String(brandKey || '').toLowerCase() === 'jotul' ? 0 : 1

const sortProductsJotulFirst = (products: ProductSearchDocument[], locale: string) =>
  [...products].sort((left, right) => {
    const brandPriority = brandSortPriority(left.brandKey) - brandSortPriority(right.brandKey)
    if (brandPriority !== 0) return brandPriority

    const primary = (left.title || left.productNumber || '').localeCompare(
      right.title || right.productNumber || '',
      locale,
      {numeric: true, sensitivity: 'base'},
    )
    if (primary !== 0) return primary
    return (left.productNumber || left._id || '').localeCompare(
      right.productNumber || right._id || '',
    )
  })

const matchesMarketScope = (marketKeys: string[], allowedMarkets: Set<string>) => {
  if (allowedMarkets.size === 0) return true
  if (marketKeys.length === 0) return true
  return marketKeys.some((marketKey) => allowedMarkets.has(marketKey))
}

const matchesFilters = (
  product: ProductSearchDocument,
  criteria: FilterCriteria,
  skip?: keyof FilterState,
) => {
  if (skip !== 'brands' && criteria.brands.size && !criteria.brands.has(product.brandKey || '')) {
    return false
  }
  if (
    skip !== 'markets' &&
    criteria.markets.size &&
    !matchesMarketScope(product.marketKeys || [], criteria.markets)
  ) {
    return false
  }
  if (
    skip !== 'productTypes' &&
    criteria.productTypes.size &&
    !criteria.productTypes.has(productTypeKeyForProduct(product))
  ) {
    return false
  }
  // Facet metadata carries `onCampaign`, set by /api/search with the same rule
  // as the campaign badge (productOnCampaign).
  if (skip !== 'campaign' && criteria.campaign.size && !product.onCampaign) return false

  return true
}

const buildFacetCounts = (
  products: ProductSearchDocument[],
  options: ProductFilterOption[],
  criteria: FilterCriteria,
  key: keyof FilterState,
) => {
  return new Map(
    options.map((option) => [
      option.value,
      products.filter((product) => {
        if (!matchesFilters(product, criteria, key)) return false

        if (key === 'brands') return product.brandKey === option.value
        if (key === 'productTypes') return productTypeKeyForProduct(product) === option.value
        if (key === 'campaign') return Boolean(product.onCampaign)
        return (product.marketKeys || []).includes(option.value)
      }).length,
    ]),
  )
}

const toggleValue = (values: string[], value: string) =>
  values.includes(value) ? values.filter((item) => item !== value) : [...values, value]

const paramsToStringArray = (searchParams: URLSearchParams, key: string) =>
  unique(searchParams.getAll(key))

const buildQueryKey = (query: string, filters: FilterState, brandUrlOverride: string[]) =>
  JSON.stringify({
    query,
    brandUrlOverride: [...brandUrlOverride].sort(),
    brands: [...filters.brands].sort(),
    markets: [...filters.markets].sort(),
    productTypes: [...filters.productTypes].sort(),
    campaign: [...filters.campaign].sort(),
  })

export function ProductCatalogClient({
  site,
  canonicalSettings,
  locale,
  initialProducts,
  initialTotal,
  facetTotal,
  productTypes,
  query: initialQuery = '',
  siteBrandKeys,
  siteMarketKeys,
  initialSelectedBrands,
  initialSelectedMarkets,
  initialSelectedProductTypes,
  initialVisibleCount,
  defaultVisibleCount,
  maxVisibleCount,
  campaignProductIds,
}: ProductCatalogClientProps) {
  const campaignIdSet = useMemo(() => new Set(campaignProductIds || []), [campaignProductIds])
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const intl = useIntl()
  const [products, setProducts] = useState<ProductSearchDocument[]>(() =>
    sortProductsJotulFirst(initialProducts, locale),
  )
  const [facetState, setFacetState] = useState<{
    scopeKey: string
    products: ProductSearchDocument[]
    loadedCount: number
  }>({scopeKey: '', products: [], loadedCount: 0})
  const [total, setTotal] = useState(initialTotal)
  const [syncedQueryKey, setSyncedQueryKey] = useState('')
  const [searchFailure, setSearchFailure] = useState<{
    key: string
    operation: 'sync' | 'more'
  } | null>(null)
  const [facetFailure, setFacetFailure] = useState<string | null>(null)
  const [searchRetry, setSearchRetry] = useState(0)
  const [facetRetry, setFacetRetry] = useState(0)
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({})
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [optimisticFilters, setOptimisticFilters] = useState<FilterState | null>(null)
  const [visibleCount, setVisibleCount] = useState(() => {
    const fromUrl = Number.parseInt(searchParams?.get('limit') || '', 10)
    if (Number.isFinite(fromUrl) && fromUrl > 0) return Math.min(fromUrl, maxVisibleCount)
    return initialVisibleCount || defaultVisibleCount
  })
  const initialFetchSizeRef = useRef(Math.max(visibleCount, defaultVisibleCount))
  const hasSyncedOnceRef = useRef(false)
  // Bumped whenever the filter-sync effect starts a new request, so a slower
  // "load more" response from a since-superseded filter state can't clobber it.
  const requestGenerationRef = useRef(0)
  const lockBrandFilters = siteBrandKeys.length > 0
  const lockMarketFilters = siteMarketKeys.length > 0

  const query = searchParams?.get('q') || initialQuery
  const brandUrlOverride = useMemo(
    () => (searchParams ? paramsToStringArray(searchParams, 'brand') : initialSelectedBrands),
    [initialSelectedBrands, searchParams],
  )
  const urlFilters = useMemo<FilterState>(
    () => ({
      brands: lockBrandFilters
        ? EMPTY_STRING_ARRAY
        : searchParams
          ? paramsToStringArray(searchParams, 'brand')
          : initialSelectedBrands,
      markets: lockMarketFilters
        ? EMPTY_STRING_ARRAY
        : searchParams
          ? paramsToStringArray(searchParams, 'market')
          : initialSelectedMarkets,
      productTypes: searchParams
        ? paramsToStringArray(searchParams, 'productType')
        : initialSelectedProductTypes,
      campaign: searchParams?.get('campaign') === '1' ? CAMPAIGN_ON : EMPTY_STRING_ARRAY,
    }),
    [
      initialSelectedBrands,
      initialSelectedMarkets,
      initialSelectedProductTypes,
      lockBrandFilters,
      lockMarketFilters,
      searchParams,
    ],
  )
  const filters = useMemo(() => {
    if (!optimisticFilters) return urlFilters
    return filterStateKey(optimisticFilters) === filterStateKey(urlFilters)
      ? urlFilters
      : optimisticFilters
  }, [optimisticFilters, urlFilters])
  const filterCriteria = useMemo(() => criteriaFromFilters(filters), [filters])
  const deferredFilterCriteria = useDeferredValue(filterCriteria)
  const currentQueryKey = useMemo(
    () => buildQueryKey(query, filters, brandUrlOverride),
    [brandUrlOverride, filters, query],
  )
  const facetScopeKey = useMemo(
    () =>
      JSON.stringify({
        brandUrlOverride: [...brandUrlOverride].sort(),
        siteBrandKeys: [...siteBrandKeys].sort(),
        siteMarketKeys: [...siteMarketKeys].sort(),
      }),
    [brandUrlOverride, siteBrandKeys, siteMarketKeys],
  )
  const facetProducts = useMemo(
    () => (facetState.scopeKey === facetScopeKey ? facetState.products : []),
    [facetScopeKey, facetState.products, facetState.scopeKey],
  )
  const loadedFacetCount = facetState.scopeKey === facetScopeKey ? facetState.loadedCount : 0
  const hasFacetFailure = facetFailure === facetScopeKey
  const isFacetHydrating = loadedFacetCount < facetTotal && !hasFacetFailure

  // JO2-247: the server marks products on campaign with the badge's rule, so it
  // needs the site's active-campaign ids, the price market and hidden prices.
  const appendCampaignContext = useCallback(
    (params: URLSearchParams) => {
      ;(campaignProductIds || []).forEach((id) => params.append('campaignIds', id))
      if (siteMarketKeys[0]) params.set('campaignMarket', siteMarketKeys[0])
      if (site.hideProductPrices) params.set('hidePrices', '1')
    },
    [campaignProductIds, site.hideProductPrices, siteMarketKeys],
  )

  const buildSearchParams = useCallback(
    (limit: number, from = 0) => {
      const params = new URLSearchParams()
      params.set('index', 'products')
      params.set('limit', String(limit))
      if (from > 0) params.set('from', String(from))
      params.set('excludeDiscontinued', '1')
      if (query) params.set('q', query)
      const effectiveBrandKeys = brandUrlOverride.length
        ? brandUrlOverride
        : lockBrandFilters
          ? siteBrandKeys
          : filters.brands.length
            ? filters.brands
            : siteBrandKeys
      const effectiveMarketKeys = lockMarketFilters
        ? siteMarketKeys
        : filters.markets.length
          ? filters.markets
          : siteMarketKeys

      effectiveBrandKeys.forEach((value) => params.append('brandKeys', value))
      effectiveMarketKeys.forEach((value) => params.append('marketKeys', value))
      filters.productTypes.forEach((value) => params.append('productTypeKeys', value))
      if (filters.campaign.length) {
        params.set('onCampaign', '1')
        appendCampaignContext(params)
      }
      return params
    },
    [
      appendCampaignContext,
      brandUrlOverride,
      filters.brands,
      filters.campaign,
      filters.markets,
      filters.productTypes,
      lockBrandFilters,
      lockMarketFilters,
      query,
      siteBrandKeys,
      siteMarketKeys,
    ],
  )

  const buildFacetSearchParams = useCallback(
    (limit: number, from = 0) => {
      const params = new URLSearchParams()
      params.set('index', 'products')
      params.set('limit', String(limit))
      params.set('facetMeta', '1')
      params.set('excludeDiscontinued', '1')
      if (from > 0) params.set('from', String(from))

      const effectiveBrandKeys = brandUrlOverride.length ? brandUrlOverride : siteBrandKeys
      effectiveBrandKeys.forEach((value) => params.append('brandKeys', value))
      siteMarketKeys.forEach((value) => params.append('marketKeys', value))
      appendCampaignContext(params)
      return params
    },
    [appendCampaignContext, brandUrlOverride, siteBrandKeys, siteMarketKeys],
  )

  useEffect(() => {
    const controller = new AbortController()
    // Bump the generation synchronously (not inside the debounce timer) so any
    // in-flight "load more" request from the prior filter state is already
    // stale by the time this effect's own request resolves.
    const generation = ++requestGenerationRef.current
    // The very first sync (mount) should honor the visible count carried in from the
    // URL's `limit` param (deep-link expectation); subsequent syncs are real filter
    // changes and should reset to a fresh first page.
    const fetchSize = hasSyncedOnceRef.current ? defaultVisibleCount : initialFetchSizeRef.current
    const timeoutId = window.setTimeout(() => {
      setSearchFailure(null)
      const params = buildSearchParams(fetchSize)
      fetch(`/api/search?${params.toString()}`, {cache: 'no-store', signal: controller.signal})
        .then(readProductSearchResponse)
        .then((data) => {
          if (requestGenerationRef.current !== generation) return
          const activeHits = sortProductsJotulFirst(
            data.hits.filter((product: ProductSearchDocument) => !isProductDiscontinued(product)),
            locale,
          )
          setProducts(activeHits)
          setTotal(data.total)
          setSyncedQueryKey(currentQueryKey)
          setIsLoadingMore(false)
          hasSyncedOnceRef.current = true
        })
        .catch(() => {
          if (controller.signal.aborted) return
          if (requestGenerationRef.current !== generation) return
          console.error('[products] search sync failed')
          setSearchFailure({key: currentQueryKey, operation: 'sync'})
          setIsLoadingMore(false)
        })
    }, FILTER_SYNC_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timeoutId)
      controller.abort()
    }
  }, [currentQueryKey, buildSearchParams, defaultVisibleCount, locale, searchRetry])

  useEffect(() => {
    if (loadedFacetCount >= facetTotal) return

    const controller = new AbortController()
    const loadNextPage = async () => {
      const params = buildFacetSearchParams(100, loadedFacetCount)

      try {
        const response = await fetch(`/api/search?${params.toString()}`, {
          cache: 'no-store',
          signal: controller.signal,
        })
        const data = await readProductSearchResponse(response)
        if (controller.signal.aborted) return
        setFacetFailure(null)
        const hits = data.hits
        if (!hits.length) {
          setFacetState((current) => ({
            scopeKey: facetScopeKey,
            products: current.scopeKey === facetScopeKey ? current.products : [],
            loadedCount: facetTotal,
          }))
          return
        }

        setFacetState((current) => {
          const currentProducts = current.scopeKey === facetScopeKey ? current.products : []
          const currentLoadedCount = current.scopeKey === facetScopeKey ? current.loadedCount : 0
          return {
            scopeKey: facetScopeKey,
            products: [...currentProducts, ...hits],
            loadedCount: currentLoadedCount + hits.length,
          }
        })
      } catch {
        if (controller.signal.aborted) return
        console.error('[products] facet hydration failed')
        setFacetFailure(facetScopeKey)
      }
    }

    void loadNextPage()

    return () => controller.abort()
  }, [buildFacetSearchParams, facetScopeKey, facetTotal, loadedFacetCount, facetRetry])

  const currentProducts = products
  const currentTotal = total
  const currentSearchFailure = searchFailure?.key === currentQueryKey ? searchFailure : null
  const isSyncingFilters = syncedQueryKey !== currentQueryKey && !currentSearchFailure
  const isBootstrappingProducts =
    currentProducts.length === 0 && currentTotal === 0 && isSyncingFilters

  const marketOptions = useMemo(
    () =>
      sortFilterOptionsByLabel(
        Array.from(new Set(facetProducts.flatMap((product) => product.marketKeys || [])))
          .filter((value): value is string => Boolean(value))
          .map((value) => ({value, label: marketLabel(value, locale)})),
        locale,
      ),
    [facetProducts, locale],
  )

  const brandOptions = useMemo(() => {
    return sortFilterOptionsByLabel(
      Array.from(
        new Map(
          facetProducts
            .filter((product) => product.brandKey)
            .map((product) => [
              product.brandKey as string,
              product.brand?.title || String(product.brandKey).toUpperCase(),
            ]),
        ).entries(),
      ).map(([value, label]) => ({value, label})),
      locale,
    )
  }, [facetProducts, locale])

  const productTypeOptions = useMemo(() => {
    const options = new Map<string, ProductFilterOption>()
    for (const option of productTypes) {
      if (option.value && option.label) options.set(option.value, option)
    }
    for (const product of facetProducts) {
      const value = productTypeKeyForProduct(product)
      if (!value || options.has(value)) continue
      options.set(value, {
        value,
        label: productTypeLabelForProduct(product, locale) || value,
      })
    }
    return sortFilterOptionsByLabel(Array.from(options.values()), locale)
  }, [facetProducts, locale, productTypes])

  const hasCampaignProducts = useMemo(
    () => facetProducts.some((product) => product.onCampaign),
    [facetProducts],
  )

  const definitions: ProductFacetDefinition<keyof FilterState>[] = useMemo(() => {
    const base = [
      ...(!lockBrandFilters
        ? [
            {
              key: 'brands' as const,
              label: intl.formatMessage({
                id: 'catalog.filters.brandsLabel',
                defaultMessage: 'Brands',
              }),
              param: 'brand',
              placeholder: intl.formatMessage({
                id: 'catalog.filters.allBrands',
                defaultMessage: 'All brands',
              }),
              options: brandOptions,
              values: filters.brands,
            },
          ]
        : []),
      ...(!lockMarketFilters
        ? [
            {
              key: 'markets' as const,
              label: intl.formatMessage({
                id: 'catalog.filters.marketsLabel',
                defaultMessage: 'Markets',
              }),
              param: 'market',
              placeholder: intl.formatMessage({
                id: 'catalog.filters.allMarkets',
                defaultMessage: 'All markets',
              }),
              options: marketOptions,
              values: filters.markets,
            },
          ]
        : []),
      {
        key: 'productTypes' as const,
        label: intl.formatMessage({
          id: 'catalog.filters.productTypeLabel',
          defaultMessage: 'Product type',
        }),
        param: 'productType',
        placeholder: intl.formatMessage({
          id: 'catalog.filters.allProductTypes',
          defaultMessage: 'All product types',
        }),
        options: productTypeOptions,
        values: filters.productTypes,
      },
      ...(hasCampaignProducts || filters.campaign.length
        ? [
            {
              key: 'campaign' as const,
              label: intl.formatMessage({
                id: 'catalog.filters.campaignLabel',
                defaultMessage: 'Campaign',
              }),
              param: 'campaign',
              placeholder: intl.formatMessage({
                id: 'catalog.filters.campaignLabel',
                defaultMessage: 'Campaign',
              }),
              options: [
                {
                  value: '1',
                  label: intl.formatMessage({
                    id: 'catalog.filters.onCampaignOnly',
                    defaultMessage: 'Only campaign products',
                  }),
                },
              ],
              values: filters.campaign,
            },
          ]
        : []),
    ]

    return base.map((definition) => ({
      key: definition.key,
      label: definition.label,
      options: definition.options,
      values: definition.values,
      counts: buildFacetCounts(
        facetProducts,
        definition.options,
        deferredFilterCriteria,
        definition.key,
      ),
    }))
  }, [
    brandOptions,
    deferredFilterCriteria,
    facetProducts,
    filters,
    hasCampaignProducts,
    intl,
    lockBrandFilters,
    lockMarketFilters,
    marketOptions,
    productTypeOptions,
  ])

  const updateFilters = (next: Partial<FilterState>, nextVisibleCount = defaultVisibleCount) => {
    const params = new URLSearchParams(searchParams?.toString() || '')
    params.delete('brand')
    params.delete('market')
    params.delete('productType')
    params.delete('campaign')
    params.delete('limit')

    const merged: FilterState = {
      brands: lockBrandFilters ? EMPTY_STRING_ARRAY : (next.brands ?? filters.brands),
      markets: lockMarketFilters ? EMPTY_STRING_ARRAY : (next.markets ?? filters.markets),
      productTypes: next.productTypes ?? filters.productTypes,
      campaign: next.campaign ?? filters.campaign,
    }
    setOptimisticFilters(merged)
    setVisibleCount(nextVisibleCount)

    if (!lockBrandFilters) merged.brands.forEach((value) => params.append('brand', value))
    if (!lockMarketFilters) merged.markets.forEach((value) => params.append('market', value))
    merged.productTypes.forEach((value) => params.append('productType', value))
    if (merged.campaign.length) params.set('campaign', '1')
    if (query) params.set('q', query)
    if (nextVisibleCount > defaultVisibleCount) params.set('limit', String(nextVisibleCount))

    window.history.replaceState(
      window.history.state,
      '',
      params.toString() ? `${pathname}?${params.toString()}` : pathname,
    )
  }

  const clearFilters = () => {
    const params = new URLSearchParams(searchParams?.toString() || '')
    params.delete('brand')
    params.delete('market')
    params.delete('productType')
    params.delete('campaign')
    params.delete('limit')
    setOptimisticFilters({
      brands: EMPTY_STRING_ARRAY,
      markets: EMPTY_STRING_ARRAY,
      productTypes: EMPTY_STRING_ARRAY,
      campaign: EMPTY_STRING_ARRAY,
    })
    setVisibleCount(defaultVisibleCount)
    window.history.replaceState(
      window.history.state,
      '',
      params.toString() ? `${pathname}?${params.toString()}` : pathname,
    )
  }

  const visibleProducts = currentProducts.slice(0, visibleCount)
  const hasMore = currentTotal > visibleProducts.length

  const loadMore = () => {
    // A filter change is mid-flight (its own sync fetch hasn't landed yet) — the
    // current `products`/`total` are stale, so pagination math here would target
    // the wrong offset. Bail rather than race the sync effect.
    if (syncedQueryKey !== currentQueryKey || isLoadingMore) return

    const remaining = Math.max(currentTotal - currentProducts.length, 0)
    const nextLimit = Math.min(
      defaultVisibleCount,
      remaining,
      Math.max(maxVisibleCount - currentProducts.length, 0),
    )
    if (nextLimit <= 0) return

    const generation = requestGenerationRef.current
    setSearchFailure(null)
    setIsLoadingMore(true)
    const params = buildSearchParams(nextLimit, currentProducts.length)
    fetch(`/api/search?${params.toString()}`, {cache: 'no-store'})
      .then(readProductSearchResponse)
      .then((data) => {
        if (requestGenerationRef.current !== generation) return
        const activeHits = sortProductsJotulFirst(
          data.hits.filter((product: ProductSearchDocument) => !isProductDiscontinued(product)),
          locale,
        )
        setProducts((current) => [...current, ...activeHits])
        setTotal(data.total)
        setSyncedQueryKey(currentQueryKey)
        setIsLoadingMore(false)
      })
      .catch(() => {
        if (requestGenerationRef.current !== generation) return
        console.error('[products] load more failed')
        setSearchFailure({key: currentQueryKey, operation: 'more'})
        setIsLoadingMore(false)
      })
  }

  const toggleSection = (key: string) => {
    setCollapsedSections((current) => ({
      ...current,
      [key]: !current[key],
    }))
  }

  const handleToggleOption = (key: string, value: string) => {
    const definition = definitions.find((item) => item.key === key)
    if (!definition) return
    pushDataLayerEvent(
      buildFilterUseEvent({
        filterType: key,
        filterValue: value,
      }),
    )
    updateFilters({
      [key]: toggleValue(definition.values, value),
    } as Partial<FilterState>)
  }

  return (
    <div className="flex gap-site-section-sm">
      {/* Filter sidebar — hidden on mobile, shown on lg. Below lg the same
          sidebar is reachable through MobileFacetSheet in the grid column. */}
      <div className="hidden w-[240px] shrink-0 lg:block">
        {isFacetHydrating ? (
          <ProductFacetSkeleton />
        ) : (
          <ProductFacetSidebar
            definitions={definitions}
            collapsedSections={collapsedSections}
            onToggleSection={toggleSection}
            onClearAll={clearFilters}
            onToggleOption={handleToggleOption}
          />
        )}
      </div>

      {/* Product grid */}
      <div className="flex-1">
        {currentSearchFailure || hasFacetFailure ? (
          <div role="alert" className="mb-site-md rounded border border-border p-site-md">
            <p className="u-body-md">
              <FormattedMessage
                id="catalog.searchFailed"
                defaultMessage="We couldn't update the products or filters. Please try again."
              />
            </p>
            <button
              type="button"
              className="mt-site-sm underline"
              onClick={() => {
                if (currentSearchFailure?.operation === 'more') loadMore()
                else if (currentSearchFailure) {
                  setSearchFailure(null)
                  setSearchRetry((value) => value + 1)
                }
                if (hasFacetFailure) {
                  setFacetFailure(null)
                  setFacetRetry((value) => value + 1)
                }
              }}
            >
              <FormattedMessage id="errors.page.tryAgain" defaultMessage="Try again" />
            </button>
          </div>
        ) : null}
        {isFacetHydrating ? null : (
          <MobileFacetSheet
            className="mb-site-md"
            definitions={definitions}
            collapsedSections={collapsedSections}
            onToggleSection={toggleSection}
            onClearAll={clearFilters}
            onToggleOption={handleToggleOption}
          />
        )}
        {isFacetHydrating ? null : (
          <ActiveFilterBadges
            className="mb-site-sm"
            definitions={definitions}
            onRemove={handleToggleOption}
            onClearAll={clearFilters}
          />
        )}
        <div className="mb-site-md flex items-center justify-between">
          {currentSearchFailure?.operation === 'sync' ? null : isSyncingFilters ? (
            <Skeleton className="h-6 w-32" />
          ) : (
            <p className="u-body-sm text-muted-foreground">
              <FormattedMessage
                id="catalog.productCount"
                defaultMessage="{n, plural, =0 {No products} one {# product} other {# products}}"
                values={{n: currentTotal}}
              />
            </p>
          )}
        </div>

        {visibleProducts.length ? (
          <>
            <ProductsGrid
              products={markActiveCampaignProducts(visibleProducts, campaignIdSet)}
              locale={locale}
              resolveHref={(product) =>
                productCanonicalPathForSite(product, site, canonicalSettings)
              }
              marketKey={siteMarketKeys[0]}
              hideProductPrices={site.hideProductPrices}
            />
            {hasMore && !currentSearchFailure ? (
              <div className="mt-site-section-sm flex justify-center">
                <LoadMoreButton
                  loading={isLoadingMore || isSyncingFilters}
                  onClick={() => {
                    const nextVisibleCount = Math.min(
                      visibleCount + defaultVisibleCount,
                      maxVisibleCount,
                    )
                    if (nextVisibleCount > currentProducts.length) loadMore()
                    updateFilters({}, nextVisibleCount)
                  }}
                />
              </div>
            ) : null}
          </>
        ) : currentSearchFailure ? null : isBootstrappingProducts ? (
          <div className="rounded border border-border bg-white p-site-lg text-center">
            <p className="u-body-md text-muted-foreground">
              <FormattedMessage id="common.loading" defaultMessage="Loading…" />
            </p>
          </div>
        ) : (
          <div className="rounded border border-dashed border-border bg-muted p-site-lg text-center">
            <p className="u-body-md text-muted-foreground">
              {currentTotal === 0 && !query ? (
                <FormattedMessage
                  id="catalog.emptyCatalogMessage"
                  defaultMessage="No products available for this site yet."
                />
              ) : (
                <FormattedMessage
                  id="catalog.noResultsMessage"
                  defaultMessage="No products match these filters."
                />
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
